import React, { useState, useEffect } from 'react';
import {
  FiEye,
  FiTrash2,
  FiX,
  FiSearch,
  FiUser,
  FiMail,
  FiPhone,
  FiMessageSquare,
  FiCalendar,
  FiRefreshCw,
  FiFilter,
  FiChevronLeft,
  FiChevronRight,
  FiClock,
  FiCheckCircle,
  FiMenu,
  FiHome,
  FiSettings,
  FiMapPin,
  FiTag
} from 'react-icons/fi';
import {
  FaEnvelope,
  FaRegEye,
  FaSort,
  FaSortUp,
  FaSortDown
} from 'react-icons/fa';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const ContactDashboard = () => {
  // State management
  const [contacts, setContacts] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [selectedContact, setSelectedContact] = useState(null);
  const [activeMenu, setActiveMenu] = useState('dashboard');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [dateFilter, setDateFilter] = useState({
    startDate: '',
    endDate: ''
  });
  const [loading, setLoading] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: 'created_at', direction: 'desc' });
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  // Check mobile screen
  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);

    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  // API base URL
  const API_BASE_URL = 'http://192.250.226.161:3002/api/leads';

  // Fetch contacts from API
  const fetchContacts = async () => {
    setLoading(true);
    try {
      const response = await fetch(API_BASE_URL);
      const data = await response.json();

      if (response.ok) {

        const processedData = data.data.map(lead => ({
          id: lead.lid,
          name: lead.name || '',
          email: lead.email || '',
          mobile: lead.mobile || '',
          city: lead.city || '',
          ltype: lead.ltype || '',
          created_at: lead.cr_date || '',
          status: lead.is_live === 1 ? 'active' : 'inactive'
        }));


        setContacts(processedData);
        toast.success('Contacts loaded successfully!');
      } else {
        throw new Error('Failed to fetch contacts');
      }
    } catch (error) {
      console.error('Error fetching contacts:', error);
      toast.error('Failed to fetch contacts. Using sample data instead.');
      // Fallback to sample data
      setContacts(getSampleContacts());
    } finally {
      setLoading(false);
    }
  };

  // Sample data for fallback
  const getSampleContacts = () => [
    {
      id: 1,
      name: 'John Smith',
      email: 'john.smith@example.com',
      mobile: '+1 (555) 123-4567',
      message: 'I am interested in learning more about your services. Please contact me at your earliest convenience.',
      created_at: '2024-03-15T10:30:00.000Z',
      status: 'new'
    },
    {
      id: 2,
      name: 'Sarah Johnson',
      email: 'sarah.j@example.com',
      mobile: '+1 (555) 987-6543',
      message: 'I have a question about your pricing plans. Could you send me more information?',
      created_at: '2024-03-14T14:20:00.000Z',
      status: 'read'
    },
    {
      id: 3,
      name: 'Mike Wilson',
      email: 'mike.wilson@example.com',
      mobile: '+1 (555) 456-7890',
      message: 'Great service! I wanted to provide some feedback about my recent experience.',
      created_at: '2024-03-13T09:15:00.000Z',
      status: 'replied'
    },
    {
      id: 4,
      name: 'Emily Davis',
      email: 'emily.davis@example.com',
      mobile: '+1 (555) 234-5678',
      message: 'I need assistance with my account. Please call me back when possible.',
      created_at: '2024-03-12T16:45:00.000Z',
      status: 'new'
    },
    {
      id: 5,
      name: 'Robert Brown',
      email: 'robert.b@example.com',
      mobile: '+1 (555) 345-6789',
      message: 'Interested in partnership opportunities. Let\'s schedule a meeting.',
      created_at: '2024-03-11T11:20:00.000Z',
      status: 'read'
    }
  ];

  // Initialize with API data
  useEffect(() => {
    fetchContacts();
  }, []);

  // Delete contact
  const deleteContact = async (id) => {
    if (window.confirm('Are you sure you want to delete this contact message?')) {
      setLoading(true);
      try {
        const response = await fetch(`${API_BASE_URL}/${id}`, {
          method: 'DELETE',
        });

        const data = await response.json();

        if (response.ok && data.success) {
          setContacts(contacts.filter(contact => contact.id !== id));
          toast.success('Contact message deleted successfully!');
        } else {
          throw new Error(data.error || 'Failed to delete contact');
        }
      } catch (error) {
        console.error('Error deleting contact:', error);
        toast.error('Error deleting contact message. Please try again.');
      } finally {
        setLoading(false);
      }
    }
  };

  // View contact details
  const viewContact = (contact) => {
    setSelectedContact(contact);
    setShowModal(true);

    // Mark as read when viewing
    if (contact.status === 'new') {
      const updatedContacts = contacts.map(c =>
        c.id === contact.id ? { ...c, status: 'read' } : c
      );
      setContacts(updatedContacts);
    }
  };

  // Handle sort
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // Filter contacts based on search, status, and date
  const filteredContacts = contacts.filter(contact => {
    const matchesSearch = contact.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      contact.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      contact.mobile.includes(searchTerm) ||
      (contact.message && contact.message.toLowerCase().includes(searchTerm.toLowerCase()));

    const matchesStatus = filterStatus === 'all' || contact.status === filterStatus;

    // Date filtering
    let matchesDate = true;
    if (dateFilter.startDate) {
      const contactDate = new Date(contact.created_at);
      const startDate = new Date(dateFilter.startDate);
      matchesDate = matchesDate && contactDate >= startDate;
    }
    if (dateFilter.endDate) {
      const contactDate = new Date(contact.created_at);
      const endDate = new Date(dateFilter.endDate);
      endDate.setHours(23, 59, 59, 999); // End of the day
      matchesDate = matchesDate && contactDate <= endDate;
    }

    return matchesSearch && matchesStatus && matchesDate;
  });

  // Sort contacts
  const sortedContacts = [...filteredContacts].sort((a, b) => {
    if (a[sortConfig.key] < b[sortConfig.key]) {
      return sortConfig.direction === 'asc' ? -1 : 1;
    }
    if (a[sortConfig.key] > b[sortConfig.key]) {
      return sortConfig.direction === 'asc' ? 1 : -1;
    }
    return 0;
  });

  // Calculate stats
  const totalContacts = contacts.length;
  const newContacts = contacts.filter(contact => contact.status === 'new').length;
  const readContacts = contacts.filter(contact => contact.status === 'read').length;
  const repliedContacts = contacts.filter(contact => contact.status === 'replied').length;

  // Pagination
  const itemsPerPage = 10;
  const totalItems = sortedContacts.length;
  const totalPagesCount = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (page - 1) * itemsPerPage;
  const paginatedContacts = sortedContacts.slice(startIndex, startIndex + itemsPerPage);

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  // Format date
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Get status badge style
  const getStatusStyle = (status) => {
    switch (status) {
      case 'new':
        return {
          backgroundColor: 'rgba(227, 99, 36, 0.15)',
          color: '#E36324'
        };
      case 'read':
        return {
          backgroundColor: 'rgba(49, 130, 206, 0.15)',
          color: '#3182CE'
        };
      case 'replied':
        return {
          backgroundColor: 'rgba(93, 195, 80, 0.15)',
          color: '#276749'
        };
      case 'active':
        return {
          backgroundColor: 'rgba(93, 195, 80, 0.15)',
          color: '#276749'
        };
      default:
        return {
          backgroundColor: 'rgba(113, 128, 150, 0.15)',
          color: '#718096'
        };
    }
  };

  // Get status icon
  const getStatusIcon = (status) => {
    switch (status) {
      case 'new':
        return <FiClock size={12} />;
      case 'read':
        return <FiEye size={12} />;
      case 'replied':
        return <FiCheckCircle size={12} />;
      case 'active':
        return <FiCheckCircle size={12} />;
      default:
        return <FiClock size={12} />;
    }
  };

  // Enhanced Responsive Styles
  const styles = {
    // Color variables
    colors: {
      primary: '#E36324',
      primaryLight: 'rgba(227, 99, 36, 0.1)',
      secondary: '#5DC350',
      secondaryLight: 'rgba(93, 195, 80, 0.1)',
      dark: '#2D3748',
      light: '#f8f9fa',
      gray: '#718096',
      danger: '#E53E3E',
      dangerLight: 'rgba(229, 62, 62, 0.1)',
      warning: '#D69E2E',
      warningLight: 'rgba(214, 158, 46, 0.1)',
      info: '#3182CE',
      infoLight: 'rgba(49, 130, 206, 0.1)',
      border: '#E2E8F0',
      shadow: '0 4px 6px rgba(0, 0, 0, 0.05)',
      shadowHover: '0 10px 15px rgba(0, 0, 0, 0.1)',
      gradient: 'linear-gradient(135deg, #E36324 0%, #FF7B42 100%)'
    },

    // Global styles
    body: {
      backgroundColor: '#F7FAFC',
      color: '#2D3748',
      lineHeight: 1.6,
      margin: 0,
      padding: 0,
      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
      minHeight: '100vh'
    },

    container: {
      maxWidth: 1400,
      margin: '0 auto',
      padding: isMobile ? '0 15px' : '0 20px'
    },

    // Header styles
    header: {
      background: 'linear-gradient(135deg, #E36324 0%, #FF7B42 100%)',
      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
      position: 'sticky',
      top: 0,
      zIndex: 100
    },

    headerContent: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: isMobile ? '15px 0' : '20px 0'
    },

    logo: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      fontSize: isMobile ? '1.2rem' : '1.5rem',
      fontWeight: 700,
      color: 'white'
    },

    userInfo: {
      display: 'flex',
      alignItems: 'center',
      gap: '15px',
      color: 'white'
    },

    avatar: {
      width: isMobile ? '35px' : '45px',
      height: isMobile ? '35px' : '45px',
      borderRadius: '50%',
      backgroundColor: 'rgba(255,255,255,0.2)',
      color: 'white',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontWeight: 'bold',
      border: '2px solid rgba(255,255,255,0.3)'
    },

    mobileMenuBtn: {
      background: 'rgba(255,255,255,0.2)',
      border: 'none',
      color: 'white',
      padding: '8px',
      borderRadius: '8px',
      cursor: 'pointer',
      display: isMobile ? 'flex' : 'none',
      alignItems: 'center',
      justifyContent: 'center'
    },

    // Main content
    mainContent: {
      display: 'flex',
      marginTop: isMobile ? '20px' : '30px',
      gap: isMobile ? '15px' : '25px',
      flexDirection: isMobile ? 'column' : 'row'
    },

    // Sidebar
    sidebar: {
      flex: isMobile ? '0 0 auto' : '0 0 280px',
      backgroundColor: 'white',
      borderRadius: '16px',
      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.05)',
      padding: isMobile ? '15px' : '20px 0',
      height: isMobile ? 'auto' : 'fit-content',
      display: isMobile && !sidebarOpen ? 'none' : 'block',
      position: isMobile ? 'fixed' : 'relative',
      top: isMobile ? '0' : 'auto',
      left: isMobile ? '0' : 'auto',
      width: isMobile ? '280px' : 'auto',
      height: isMobile ? '100vh' : 'auto',
      zIndex: isMobile ? 99 : 'auto',
      overflowY: isMobile ? 'auto' : 'visible'
    },

    sidebarOverlay: {
      display: isMobile && sidebarOpen ? 'block' : 'none',
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.5)',
      zIndex: 98
    },

    sidebarMenu: {
      listStyle: 'none',
      margin: 0,
      padding: 0
    },

    sidebarMenuItem: {
      padding: isMobile ? '14px 20px' : '16px 25px',
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      cursor: 'pointer',
      transition: 'all 0.3s',
      borderLeft: '4px solid transparent',
      fontSize: isMobile ? '14px' : '15px',
      color: '#718096',
      textDecoration: 'none'
    },

    sidebarMenuItemActive: {
      backgroundColor: 'rgba(227, 99, 36, 0.1)',
      color: '#E36324',
      borderLeft: '4px solid #E36324'
    },

    // Content area
    content: {
      flex: 1,
      backgroundColor: 'white',
      borderRadius: '16px',
      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.05)',
      padding: isMobile ? '20px' : '30px',
      border: '1px solid #E2E8F0'
    },

    contentHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: isMobile ? 'flex-start' : 'center',
      marginBottom: isMobile ? '20px' : '30px',
      paddingBottom: isMobile ? '15px' : '25px',
      borderBottom: '2px solid #F7FAFC',
      flexDirection: isMobile ? 'column' : 'row',
      gap: isMobile ? '15px' : '0'
    },

    contentTitle: {
      fontSize: isMobile ? '1.4rem' : '1.75rem',
      fontWeight: 700,
      margin: 0,
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      color: '#2D3748'
    },

    headerActions: {
      display: 'flex',
      gap: '10px',
      flexWrap: 'wrap'
    },

    // Buttons
    btn: {
      padding: isMobile ? '10px 16px' : '12px 20px',
      borderRadius: '10px',
      border: 'none',
      cursor: 'pointer',
      fontWeight: 600,
      display: 'inline-flex',
      alignItems: 'center',
      gap: '8px',
      transition: 'all 0.3s ease',
      fontSize: isMobile ? '13px' : '14px',
      fontFamily: 'inherit',
      whiteSpace: 'nowrap'
    },

    btnPrimary: {
      background: 'linear-gradient(135deg, #E36324 0%, #FF7B42 100%)',
      color: 'white',
      boxShadow: '0 2px 4px rgba(227, 99, 36, 0.3)',
      ':hover': {
        transform: 'translateY(-2px)',
        boxShadow: '0 4px 8px rgba(227, 99, 36, 0.4)'
      }
    },

    btnSecondary: {
      backgroundColor: '#5DC350',
      color: 'white',
      boxShadow: '0 2px 4px rgba(93, 195, 80, 0.3)',
      ':hover': {
        transform: 'translateY(-2px)',
        boxShadow: '0 4px 8px rgba(93, 195, 80, 0.4)'
      }
    },

    btnDanger: {
      backgroundColor: '#E53E3E',
      color: 'white',
      boxShadow: '0 2px 4px rgba(229, 62, 62, 0.3)',
      ':hover': {
        transform: 'translateY(-2px)',
        boxShadow: '0 4px 8px rgba(229, 62, 62, 0.4)'
      }
    },

    btnOutline: {
      backgroundColor: 'transparent',
      color: '#718096',
      border: '2px solid #E2E8F0',
      ':hover': {
        backgroundColor: '#F7FAFC',
        borderColor: '#E36324',
        color: '#E36324'
      }
    },

    // Stats cards
    statsCards: {
      display: 'grid',
      gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(auto-fit, minmax(220px, 1fr))',
      gap: isMobile ? '15px' : '20px',
      marginBottom: isMobile ? '20px' : '30px'
    },

    statCard: {
      backgroundColor: 'white',
      borderRadius: '12px',
      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.05)',
      padding: isMobile ? '20px' : '25px',
      display: 'flex',
      flexDirection: 'column',
      gap: '12px',
      transition: 'transform 0.3s, box-shadow 0.3s',
      border: '1px solid #F0F0F0',
      ':hover': {
        transform: 'translateY(-2px)',
        boxShadow: '0 6px 12px rgba(0, 0, 0, 0.1)'
      }
    },

    statValue: {
      fontSize: isMobile ? '1.5rem' : '2rem',
      fontWeight: 700,
      color: '#E36324'
    },

    statLabel: {
      color: '#718096',
      fontSize: isMobile ? '12px' : '14px',
      fontWeight: 500
    },

    statIcon: {
      width: isMobile ? '40px' : '50px',
      height: isMobile ? '40px' : '50px',
      borderRadius: '10px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: 'white',
      fontSize: isMobile ? '1.2rem' : '1.5rem'
    },

    statIconPrimary: {
      backgroundColor: '#E36324'
    },

    statIconSecondary: {
      backgroundColor: '#5DC350'
    },

    statIconInfo: {
      backgroundColor: '#3182CE'
    },

    statIconWarning: {
      backgroundColor: '#D69E2E'
    },

    // Search and filter
    searchFilterContainer: {
      display: 'flex',
      gap: isMobile ? '10px' : '15px',
      marginBottom: isMobile ? '20px' : '25px',
      flexWrap: 'wrap',
      flexDirection: isMobile ? 'column' : 'row'
    },

    searchInput: {
      flex: isMobile ? '0 0 100%' : 1,
      minWidth: isMobile ? 'auto' : '250px',
      padding: isMobile ? '12px 12px 12px 40px' : '12px 12px 12px 40px',
      border: '1px solid #E2E8F0',
      borderRadius: '10px',
      fontSize: isMobile ? '14px' : '1rem',
      transition: 'border-color 0.3s, box-shadow 0.3s',
      fontFamily: 'inherit'
    },

    filterSelect: {
      padding: isMobile ? '12px' : '12px',
      border: '1px solid #E2E8F0',
      borderRadius: '10px',
      fontSize: isMobile ? '14px' : '1rem',
      backgroundColor: 'white',
      minWidth: isMobile ? '100%' : '150px',
      fontFamily: 'inherit'
    },

    dateFilterContainer: {
      display: 'flex',
      gap: '10px',
      flexWrap: 'wrap'
    },

    dateInput: {
      padding: isMobile ? '10px' : '12px',
      border: '1px solid #E2E8F0',
      borderRadius: '8px',
      fontSize: isMobile ? '14px' : '1rem',
      fontFamily: 'inherit',
      minWidth: isMobile ? '140px' : '150px'
    },

    // Table styles
    tableContainer: {
      overflowX: 'auto',
      borderRadius: '12px',
      border: '1px solid #E2E8F0',
      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
      WebkitOverflowScrolling: 'touch'
    },

    table: {
      width: '100%',
      borderCollapse: 'collapse',
      minWidth: isMobile ? '800px' : '900px'
    },

    tableHeader: {
      padding: isMobile ? '12px' : '16px',
      textAlign: 'left',
      borderBottom: '2px solid #E2E8F0',
      backgroundColor: '#F7FAFC',
      fontWeight: 600,
      color: '#2D3748',
      cursor: 'pointer',
      transition: 'all 0.3s',
      fontSize: isMobile ? '13px' : '14px',
      ':hover': {
        backgroundColor: '#EDF2F7'
      }
    },

    tableCell: {
      padding: isMobile ? '12px' : '16px',
      textAlign: 'left',
      borderBottom: '1px solid #E2E8F0',
      fontSize: isMobile ? '13px' : '14px'
    },

    statusBadge: {
      padding: '6px 12px',
      borderRadius: '20px',
      fontSize: '12px',
      fontWeight: 600,
      display: 'inline-flex',
      alignItems: 'center',
      gap: '4px'
    },

    actionButtons: {
      display: 'flex',
      gap: '6px',
      flexWrap: 'wrap'
    },

    actionBtn: {
      padding: '6px 10px',
      borderRadius: '8px',
      border: 'none',
      cursor: 'pointer',
      fontSize: '11px',
      transition: 'all 0.3s',
      display: 'flex',
      alignItems: 'center',
      gap: '4px',
      fontWeight: 500,
      whiteSpace: 'nowrap'
    },

    actionView: {
      backgroundColor: 'rgba(49, 130, 206, 0.1)',
      color: '#3182CE',
      border: '1px solid rgba(49, 130, 206, 0.2)',
      ':hover': {
        backgroundColor: 'rgba(49, 130, 206, 0.2)',
        transform: 'translateY(-1px)'
      }
    },

    actionDelete: {
      backgroundColor: 'rgba(229, 62, 62, 0.1)',
      color: '#E53E3E',
      border: '1px solid rgba(229, 62, 62, 0.2)',
      ':hover': {
        backgroundColor: 'rgba(229, 62, 62, 0.2)',
        transform: 'translateY(-1px)'
      }
    },

    // Modal styles
    modal: {
      display: 'flex',
      position: 'fixed',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      backgroundColor: 'rgba(0, 0, 0, 0.6)',
      zIndex: 1000,
      alignItems: 'center',
      justifyContent: 'center',
      padding: isMobile ? '10px' : '20px',
      backdropFilter: 'blur(4px)'
    },

    modalContent: {
      backgroundColor: 'white',
      borderRadius: '16px',
      width: '100%',
      maxWidth: isMobile ? '100%' : '600px',
      boxShadow: '0 20px 25px rgba(0, 0, 0, 0.15)',
      overflow: 'hidden',
      maxHeight: '90vh',
      overflowY: 'auto'
    },

    modalHeader: {
      padding: isMobile ? '20px' : '25px',
      background: 'linear-gradient(135deg, #E36324 0%, #FF7B42 100%)',
      color: 'white',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center'
    },

    modalTitle: {
      fontSize: isMobile ? '1.2rem' : '1.4rem',
      fontWeight: 600,
      margin: 0,
      display: 'flex',
      alignItems: 'center',
      gap: '10px'
    },

    closeBtn: {
      background: 'rgba(255,255,255,0.2)',
      border: 'none',
      color: 'white',
      fontSize: '1.5rem',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: isMobile ? '32px' : '36px',
      height: isMobile ? '32px' : '36px',
      borderRadius: '8px',
      transition: 'background-color 0.3s',
      ':hover': {
        backgroundColor: 'rgba(255,255,255,0.3)'
      }
    },

    modalBody: {
      padding: isMobile ? '20px' : '30px'
    },

    contactDetail: {
      marginBottom: '20px',
      padding: '15px',
      backgroundColor: '#F7FAFC',
      borderRadius: '8px',
      border: '1px solid #E2E8F0'
    },

    contactField: {
      display: 'flex',
      alignItems: 'flex-start',
      gap: '12px',
      marginBottom: '12px',
      paddingBottom: '12px',
      borderBottom: '1px solid #E2E8F0',
      ':last-child': {
        marginBottom: 0,
        paddingBottom: 0,
        borderBottom: 'none'
      }
    },

    contactIcon: {
      color: '#E36324',
      fontSize: '18px',
      marginTop: '2px',
      flexShrink: 0
    },

    contactText: {
      flex: 1
    },

    contactLabel: {
      fontWeight: 600,
      color: '#2D3748',
      fontSize: '14px',
      marginBottom: '4px'
    },

    contactValue: {
      color: '#718096',
      fontSize: '14px',
      lineHeight: 1.5
    },

    modalFooter: {
      padding: isMobile ? '20px' : '25px',
      backgroundColor: '#F7FAFC',
      display: 'flex',
      justifyContent: 'flex-end',
      gap: '12px',
      borderTop: '1px solid #E2E8F0',
      flexWrap: 'wrap'
    },

    // Loading state
    loadingSpinner: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      padding: '40px',
      flexDirection: 'column',
      gap: '15px'
    },

    // Empty state
    emptyState: {
      textAlign: 'center',
      padding: '40px',
      color: '#718096'
    },

    // Pagination
    pagination: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      gap: '8px',
      marginTop: '20px',
      padding: '20px',
      flexWrap: 'wrap'
    },

    pageButton: {
      padding: '8px 12px',
      border: '1px solid #E2E8F0',
      backgroundColor: 'white',
      borderRadius: '8px',
      cursor: 'pointer',
      transition: 'all 0.3s',
      fontSize: '14px',
      minWidth: '40px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      ':hover': {
        backgroundColor: '#F7FAFC'
      }
    },

    activePage: {
      backgroundColor: '#E36324',
      color: 'white',
      borderColor: '#E36324',
      ':hover': {
        backgroundColor: '#d4561d'
      }
    },

    // Contact card for mobile
    contactCard: {
      backgroundColor: 'white',
      borderRadius: '12px',
      border: '1px solid #E2E8F0',
      padding: '16px',
      marginBottom: '12px',
      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)'
    },

    contactCardHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      marginBottom: '12px'
    },

    contactCardTitle: {
      fontSize: '16px',
      fontWeight: 600,
      margin: '0 0 8px 0',
      color: '#2D3748'
    },

    contactCardMeta: {
      display: 'flex',
      gap: '12px',
      fontSize: '12px',
      color: '#718096',
      marginBottom: '12px',
      flexWrap: 'wrap'
    },

    contactCardMessage: {
      fontSize: '13px',
      color: '#718096',
      lineHeight: 1.4,
      marginBottom: '12px',
      display: '-webkit-box',
      WebkitLineClamp: 2,
      WebkitBoxOrient: 'vertical',
      overflow: 'hidden'
    },

    contactCardActions: {
      display: 'flex',
      gap: '8px',
      justifyContent: 'flex-end'
    }
  };

  // Render contact cards for mobile view
  const renderContactCards = () => {
    return paginatedContacts.map(contact => (
      <div key={contact.id} style={styles.contactCard}>
        <div style={styles.contactCardHeader}>
          <div style={{ flex: 1 }}>
            <h3 style={styles.contactCardTitle}>{contact.name}</h3>
            <div style={styles.contactCardMeta}>
              <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <FiMail size={10} />
                {contact.email}
              </span>
              <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <FiPhone size={10} />
                {contact.mobile}
              </span>
            </div>
          </div>
          <div style={{
            ...styles.statusBadge,
            ...getStatusStyle(contact.status)
          }}>
            {getStatusIcon(contact.status)}
            {contact.status.charAt(0).toUpperCase() + contact.status.slice(1)}
          </div>
        </div>

        <div style={styles.contactCardMessage}>
          {contact.message}
        </div>

        <div style={styles.contactCardMeta}>
          <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <FiCalendar size={10} />
            {formatDate(contact.created_at)}
          </span>
        </div>

        <div style={styles.contactCardActions}>
          <button
            style={{ ...styles.actionBtn, ...styles.actionView }}
            onClick={() => viewContact(contact)}
          >
            <FiEye size={12} />
            View
          </button>
          <button
            style={{ ...styles.actionBtn, ...styles.actionDelete }}
            onClick={() => deleteContact(contact.id)}
          >
            <FiTrash2 size={12} />
            Delete
          </button>
        </div>
      </div>
    ));
  };

  return (
    <div style={styles.body}>
      {/* Toast Container */}
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />


      {/* Sidebar Overlay for Mobile */}
      {isMobile && (
        <div
          style={styles.sidebarOverlay}
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main Content */}
      <div style={styles.container}>
        <div style={styles.mainContent}>
          {/* Sidebar */}

          {/* Content Area */}
          <main style={styles.content}>
            <div style={styles.contentHeader}>
              <h2 style={styles.contentTitle}>
                <FaEnvelope />
                Contact Messages
              </h2>
              <div style={styles.headerActions}>
                <button
                  style={{ ...styles.btn, ...styles.btnOutline }}
                  onClick={fetchContacts}
                  disabled={loading}
                >
                  <FiRefreshCw size={16} />
                  {!isMobile && 'Refresh'}
                </button>
              </div>
            </div>

            {/* Stats Cards */}
            <div style={styles.statsCards}>
              <div style={styles.statCard}>
                <div style={{ ...styles.statIcon, ...styles.statIconPrimary }}>
                  <FaEnvelope size={isMobile ? 16 : 20} />
                </div>
                <div style={styles.statValue}>{totalContacts}</div>
                <div style={styles.statLabel}>Total Messages</div>
              </div>
              <div style={styles.statCard}>
                <div style={{ ...styles.statIcon, ...styles.statIconWarning }}>
                  <FiClock size={isMobile ? 16 : 20} />
                </div>
                <div style={styles.statValue}>{newContacts}</div>
                <div style={styles.statLabel}>New Messages</div>
              </div>
              <div style={styles.statCard}>
                <div style={{ ...styles.statIcon, ...styles.statIconInfo }}>
                  <FiEye size={isMobile ? 16 : 20} />
                </div>
                <div style={styles.statValue}>{readContacts}</div>
                <div style={styles.statLabel}>Read Messages</div>
              </div>
              <div style={styles.statCard}>
                <div style={{ ...styles.statIcon, ...styles.statIconSecondary }}>
                  <FiCheckCircle size={isMobile ? 16 : 20} />
                </div>
                <div style={styles.statValue}>{repliedContacts}</div>
                <div style={styles.statLabel}>Replied Messages</div>
              </div>
            </div>

            {/* Search and Filter */}
            <div style={styles.searchFilterContainer}>
              <div style={{ position: 'relative', flex: isMobile ? '0 0 100%' : 1, minWidth: isMobile ? 'auto' : '250px' }}>
                <FiSearch
                  style={{
                    position: 'absolute',
                    left: '12px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    color: '#718096'
                  }}
                  size={18}
                />
                <input
                  type="text"
                  placeholder="Search contacts..."
                  style={styles.searchInput}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>

              <select
                style={styles.filterSelect}
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
              >
                <option value="all">All Status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>

              </select>

              <div style={styles.dateFilterContainer}>
                <input
                  type="date"
                  style={styles.dateInput}
                  value={dateFilter.startDate}
                  onChange={(e) => setDateFilter({ ...dateFilter, startDate: e.target.value })}
                  placeholder="Start Date"
                />
                <input
                  type="date"
                  style={styles.dateInput}
                  value={dateFilter.endDate}
                  onChange={(e) => setDateFilter({ ...dateFilter, endDate: e.target.value })}
                  placeholder="End Date"
                />
                {(dateFilter.startDate || dateFilter.endDate) && (
                  <button
                    style={{ ...styles.btn, ...styles.btnOutline, padding: '8px 12px' }}
                    onClick={() => setDateFilter({ startDate: '', endDate: '' })}
                  >
                    Clear Dates
                  </button>
                )}
              </div>
            </div>

            {/* Data Table (Desktop) / Cards (Mobile) */}
            {isMobile ? (
              // Mobile Cards View
              <div>
                {loading ? (
                  <div style={styles.loadingSpinner}>
                    <FiRefreshCw size={24} style={{ animation: 'spin 1s linear infinite' }} />
                    <div>Loading contacts...</div>
                  </div>
                ) : paginatedContacts.length === 0 ? (
                  <div style={styles.emptyState}>
                    <FaEnvelope size={48} style={{ marginBottom: '10px', opacity: 0.5 }} />
                    <h3>No contact messages found</h3>
                    <p>Try adjusting your search or filter criteria</p>
                  </div>
                ) : (
                  renderContactCards()
                )}
              </div>
            ) : (
              // Desktop Table View
              <div style={styles.tableContainer}>
                {loading ? (
                  <div style={styles.loadingSpinner}>
                    <FiRefreshCw size={24} style={{ animation: 'spin 1s linear infinite' }} />
                    <div>Loading contacts...</div>
                  </div>
                ) : paginatedContacts.length === 0 ? (
                  <div style={styles.emptyState}>
                    <FaEnvelope size={48} style={{ marginBottom: '10px', opacity: 0.5 }} />
                    <h3>No contact messages found</h3>
                    <p>Try adjusting your search or filter criteria</p>
                  </div>
                ) : (
                  <table style={styles.table}>
                    <thead>
                      <tr>
                        <th
                          style={styles.tableHeader}
                          onClick={() => handleSort('name')}
                        >
                          <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                            Name
                            {sortConfig.key === 'name' && (
                              sortConfig.direction === 'asc' ? <FaSortUp /> : <FaSortDown />
                            )}
                          </div>
                        </th>
                        <th
                          style={styles.tableHeader}
                          onClick={() => handleSort('email')}
                        >
                          <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                            Email
                            {sortConfig.key === 'email' && (
                              sortConfig.direction === 'asc' ? <FaSortUp /> : <FaSortDown />
                            )}
                          </div>
                        </th>
                        <th style={styles.tableHeader}>Mobile</th>
                        {/* <th style={styles.tableHeader}>Type</th> */}
                        <th style={styles.tableHeader}>City</th>
                        <th style={styles.tableHeader}>Type</th>

                        <th
                          style={styles.tableHeader}
                          onClick={() => handleSort('status')}
                        >
                          <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                            Status
                            {sortConfig.key === 'status' && (
                              sortConfig.direction === 'asc' ? <FaSortUp /> : <FaSortDown />
                            )}
                          </div>
                        </th>
                        <th
                          style={styles.tableHeader}
                          onClick={() => handleSort('created_at')}
                        >
                          <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                            Date
                            {sortConfig.key === 'created_at' && (
                              sortConfig.direction === 'asc' ? <FaSortUp /> : <FaSortDown />
                            )}
                          </div>
                        </th>
                        <th style={styles.tableHeader}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedContacts.map(contact => (
                        <tr key={contact.id}>
                          <td style={styles.tableCell}>
                            <strong>{contact.name}</strong>
                          </td>
                          <td style={styles.tableCell}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <FiMail size={14} style={{ color: '#718096' }} />
                              {contact.email}
                            </div>
                          </td>
                          <td style={styles.tableCell}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <FiPhone size={14} style={{ color: '#718096' }} />
                              {contact.mobile}
                            </div>
                          </td>
                          {/* <td style={styles.tableCell}>
                            <div style={{
                              maxWidth: '200px',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap'
                            }}>
                              {contact.message}
                            </div>
                          </td> */}
                          <td style={styles.tableCell}>

                            <div style={{
                              maxWidth: '200px',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap'
                            }}>
                              {contact.city}
                            </div>
                          </td>
                          <td style={styles.tableCell}>
                            <div style={{
                              maxWidth: '200px',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap'
                            }}>
                              {contact.ltype}
                            </div>
                          </td>

                          <td style={styles.tableCell}>
                            <div style={{
                              ...styles.statusBadge,
                              ...getStatusStyle(contact.status)
                            }}>
                              {getStatusIcon(contact.status)}
                              {contact.status.charAt(0).toUpperCase() + contact.status.slice(1)}
                            </div>
                          </td>
                          <td style={styles.tableCell}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                              <FiCalendar size={12} style={{ color: '#718096' }} />
                              {formatDate(contact.created_at)}
                            </div>
                          </td>
                          <td style={styles.tableCell}>
                            <div style={styles.actionButtons}>
                              <button
                                style={{ ...styles.actionBtn, ...styles.actionView }}
                                onClick={() => viewContact(contact)}
                              >
                                <FiEye size={14} />
                                View
                              </button>
                              <button
                                style={{ ...styles.actionBtn, ...styles.actionDelete }}
                                onClick={() => deleteContact(contact.id)}
                              >
                                <FiTrash2 size={14} />
                                Delete
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            )}

            {/* Pagination */}
            {totalPagesCount > 1 && (
              <div style={styles.pagination}>
                <button
                  style={styles.pageButton}
                  onClick={() => handlePageChange(page - 1)}
                  disabled={page === 1}
                >
                  <FiChevronLeft />
                </button>
                {[...Array(totalPagesCount)].map((_, index) => (
                  <button
                    key={index + 1}
                    style={{
                      ...styles.pageButton,
                      ...(page === index + 1 ? styles.activePage : {})
                    }}
                    onClick={() => handlePageChange(index + 1)}
                  >
                    {index + 1}
                  </button>
                ))}
                <button
                  style={styles.pageButton}
                  onClick={() => handlePageChange(page + 1)}
                  disabled={page === totalPagesCount}
                >
                  <FiChevronRight />
                </button>
              </div>
            )}
          </main>
        </div>
      </div>

      {/* Contact Detail Modal */}
      {showModal && selectedContact && (
        <div style={styles.modal}>
          <div style={styles.modalContent}>
            <div style={styles.modalHeader}>
              <h3 style={styles.modalTitle}>
                <FaEnvelope />
                Contact Message Details
              </h3>
              <button
                style={styles.closeBtn}
                onClick={() => setShowModal(false)}
              >
                <FiX />
              </button>
            </div>
            <div style={styles.modalBody}>
              <div style={styles.contactDetail}>
                <div style={styles.contactField}>
                  <FiUser style={styles.contactIcon} />
                  <div style={styles.contactText}>
                    <div style={styles.contactLabel}>Name</div>
                    <div style={styles.contactValue}>{selectedContact.name}</div>
                  </div>
                </div>

                <div style={styles.contactField}>
                  <FiMail style={styles.contactIcon} />
                  <div style={styles.contactText}>
                    <div style={styles.contactLabel}>Email</div>
                    <div style={styles.contactValue}>{selectedContact.email}</div>
                  </div>
                </div>

                <div style={styles.contactField}>
                  <FiPhone style={styles.contactIcon} />
                  <div style={styles.contactText}>
                    <div style={styles.contactLabel}>Mobile</div>
                    <div style={styles.contactValue}>{selectedContact.mobile}</div>
                  </div>
                </div>

                <div style={styles.contactField}>
                  <FiCalendar style={styles.contactIcon} />
                  <div style={styles.contactText}>
                    <div style={styles.contactLabel}>Date Received</div>
                    <div style={styles.contactValue}>{formatDate(selectedContact.created_at)}</div>
                  </div>
                </div>

                <div style={styles.contactField}>
                  <div style={{
                    ...styles.statusBadge,
                    ...getStatusStyle(selectedContact.status)
                  }}>
                    {getStatusIcon(selectedContact.status)}
                    Status: {selectedContact.status.charAt(0).toUpperCase() + selectedContact.status.slice(1)}
                  </div>
                </div>

                {/* <div style={styles.contactField}>
                  <FiMessageSquare style={styles.contactIcon} />
                  <div style={styles.contactText}>
                    <div style={styles.contactLabel}>Message</div>
                    <div style={{ ...styles.contactValue, whiteSpace: 'pre-wrap' }}>
                      {selectedContact.message}
                    </div>
                  </div>
                </div> */}
                {/* City */}
                <div style={styles.contactField}>
                  <FiMapPin style={styles.contactIcon} />
                  <div style={styles.contactText}>
                    <div style={styles.contactLabel}>City</div>
                    <div style={styles.contactValue}>{selectedContact.city || "—"}</div>
                  </div>
                </div>

                {/* Type */}
                <div style={styles.contactField}>
                  <FiTag style={styles.contactIcon} />
                  <div style={styles.contactText}>
                    <div style={styles.contactLabel}>Contact Type</div>
                    <div style={styles.contactValue}>{selectedContact.ltype || "—"}</div>
                  </div>
                </div>

              </div>
            </div>
            <div style={styles.modalFooter}>
              <button
                style={{ ...styles.btn, ...styles.btnOutline }}
                onClick={() => setShowModal(false)}
              >
                <FiX style={{ marginRight: '5px' }} />
                Close
              </button>
              <button
                style={{ ...styles.btn, ...styles.btnDanger }}
                onClick={() => {
                  deleteContact(selectedContact.id);
                  setShowModal(false);
                }}
              >
                <FiTrash2 style={{ marginRight: '5px' }} />
                Delete Message
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContactDashboard;